import { Button } from '@synoptic/ui-kit/button/button.js';
import { Checkbox } from '@synoptic/ui-kit/checkbox/checkbox.tsx';
import { Input } from '@synoptic/ui-kit/input/input.tsx';
import { useIsTablet } from '@synoptic/ui-kit/responsive/hooks.ts';
import { FC, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { AcceptTerms } from '@/components/streams/stream-form/accept-terms.tsx';
import { PriceInfo } from '@/components/streams/stream-form/price-guidelines.tsx';
import {
  columnFields,
  paidStreamDataContainer,
  streamFormActions,
  checkbox,
} from '@/components/streams/stream-form/stream-form.css.ts';
import { StreamFormInputs } from '@/components/streams/stream-form/stream-form.tsx';

type PaidStreamDataStepProps = {
  onBack: () => void;
  hasAcceptedPolicy: boolean;
  onAcceptPolicy: (accepted: boolean) => void;
  hasInitialValues?: boolean;
};
export const PaidStreamDataStep: FC<PaidStreamDataStepProps> = ({
  onBack,
  hasAcceptedPolicy,
  onAcceptPolicy,
  hasInitialValues,
}) => {
  const isTablet = useIsTablet();

  const { formState, register, getValues, setValue } =
    useFormContext<StreamFormInputs>();

  const [showOptionalPrices, setShowOptionalPrices] = useState(
    () =>
      !!getValues('paidData.threeMonthPrice') ||
      !!getValues('paidData.sixMonthPrice') ||
      !!getValues('paidData.yearlyPrice'),
  );

  const handleToggleOptionalPrices = (checked: boolean) => {
    setShowOptionalPrices(checked);
    if (!checked) {
      setValue('paidData.threeMonthPrice', null);
      setValue('paidData.sixMonthPrice', null);
      setValue('paidData.yearlyPrice', null);
    }
  };

  return (
    <div className={paidStreamDataContainer}>
      <div>
        <PriceInfo />

        <Input
          autoFocus={hasInitialValues}
          {...register('paidData.monthlyPrice', {
            required: 'Price is required',
            validate: (value) =>
              (value! > 0 && value! <= 10000) ||
              'Price must be greater than 0 and less than or equal to 10,000 USD$',
          })}
          type={'number'}
          errorMessage={formState.errors.paidData?.monthlyPrice?.message}
          placeholder={'Fill in monthly price in USD$'}
          label={'Monthly price'}
          disabled={hasInitialValues}
        />
      </div>

      {!hasInitialValues && (
        <div>
          <label className={checkbox}>
            <Checkbox
              checked={showOptionalPrices}
              onCheckedChange={(val) => handleToggleOptionalPrices(!!val)}
            />
            Add discounted prices for longer periods
          </label>
        </div>
      )}

      {(showOptionalPrices || hasInitialValues) && (
        <div className={columnFields}>
          <Input
            {...register('paidData.threeMonthPrice', {
              validate: (value) =>
                !value ||
                (value > 0 && value <= 30000) ||
                'Price must be greater than 0 and less than or equal to 30,000 USD$',
            })}
            type={'number'}
            errorMessage={formState.errors.paidData?.threeMonthPrice?.message}
            placeholder={'Price for 3 months in USD$'}
            label={'3-month price'}
            disabled={hasInitialValues}
          />

          <Input
            {...register('paidData.sixMonthPrice', {
              validate: (value) =>
                !value ||
                (value > 0 && value <= 60000) ||
                'Price must be greater than 0 and less than or equal to 60,000 USD$',
            })}
            type={'number'}
            errorMessage={formState.errors.paidData?.sixMonthPrice?.message}
            placeholder={'Price for 6 months in USD$'}
            label={'6-month price'}
            disabled={hasInitialValues}
          />

          <Input
            {...register('paidData.yearlyPrice', {
              validate: (value) =>
                !value ||
                (value > 0 && value <= 120000) ||
                'Price must be greater than 0 and less than or equal to 120,000 USD$',
            })}
            type={'number'}
            errorMessage={formState.errors.paidData?.yearlyPrice?.message}
            placeholder={'Price for 1 year in USD$'}
            label={'1-year price'}
            disabled={hasInitialValues}
          />
        </div>
      )}

      <AcceptTerms accepted={hasAcceptedPolicy} setAccepted={onAcceptPolicy} />

      <div className={streamFormActions}>
        <Button
          onClick={(e) => {
            e.preventDefault();
            onBack();
          }}
          disabled={formState.isSubmitting}
          size={isTablet ? 'large' : 'medium'}
          variant={'secondary'}
          type={'button'}
        >
          Back
        </Button>
        <Button
          loading={formState.isSubmitting}
          disabled={!formState.isValid || !hasAcceptedPolicy}
          type="submit"
          size={isTablet ? 'large' : 'medium'}
        >
          {hasInitialValues ? 'Save' : 'Create'}
        </Button>
      </div>
    </div>
  );
};
